import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DrawPrizeDto, LandingAward } from '~shared/schemas';
import { drawErrorCode } from '~/lib/api';
import { useLandingAwards, useLandingDraw } from '~/hooks/use-landing-draw';
import { prefersReducedMotion } from './effects/motion-prefs';
import { WinModal } from './modals/win-modal';
import { PrizeTypeImage } from './prize-type-image';
import { LockedBadge } from './locked-badge';
import { MessageText } from './message-text';

type CellStatus = 'closed' | 'busy' | 'open';
interface Cell {
  status: CellStatus;
  prize: DrawPrizeDto | null;
  ghost: boolean;
  winner: boolean;
}
const CELL_COUNT = 9;
/** Минимальная длительность «козочка открывает карточку» — быстрый ответ сервера не обрывает анимацию. */
const OPEN_ANIMATION_MS = 700;

interface InstantLotteryProps {
  /** Участие не подтверждено или человек не участвует — поле, счётчик и «Мои подарки» закрыты замочком.
   *  Без неоткрытых козочек поле закрывается замочком само (требование «Замочек на поле лотереи без козочек»). */
  locked: boolean;
  /** Неоткрытые козочки из состояния лендинга; null — пока закрыто замочком. Ответ розыгрыша обновляет его в кэше. */
  balance: number | null;
  /** Конфетти (эффект use-confetti из landing.tsx) по координатам клика. */
  burst: (x: number, y: number, n?: number, power?: number) => void;
}

/**
 * Моментальная лотерея (`section#gifts` макета): поле 3×3 с настоящим розыгрышем и «Мои подарки» (connect-landing-draw).
 * Приз карточки и остальных 8 ячеек — только из ответа POST /api/landing/draw: до ответа фронт призов не знает.
 *
 * AICODE-NOTE: choreography упрощена относительно js/main.js — там карточка открывается
 * «козочкой», прискакивающей через весь экран по рассчитанным координатам (requestAnimationFrame
 * + getBoundingClientRect между элементами). Здесь тот же видимый результат (переворот карточки,
 * конфетти, модалка выигрыша, показ остальных призов и перетасовка) получен через CSS-классы
 * уже заложенные в landing.css (`.gift.busy/.open/.hit/.ghost/.winner`, `.gifts.shuffle`) —
 * полёт козочки по экрану как отдельная флуоресцентная деталь не перенесён, спекой не требуется.
 */
export function InstantLottery({ locked, balance, burst }: InstantLotteryProps) {
  const { t } = useTranslation('landing');
  const draw = useLandingDraw();
  const awards = useLandingAwards(!locked);
  const [cells, setCells] = useState<Cell[]>(() =>
    Array.from({ length: CELL_COUNT }, () => ({ status: 'closed', prize: null, ghost: false, winner: false })),
  );
  const [roundNote, setRoundNote] = useState('');
  const [shuffle, setShuffle] = useState(false);
  const [running, setRunning] = useState(false);
  const [winModal, setWinModal] = useState<{ prize: DrawPrizeDto; popupMessage: string | null } | null>(null);
  const [showAllMyGifts, setShowAllMyGifts] = useState(false);
  // Текущий раунд: выбранная ячейка и призы остальных 8 из ответа розыгрыша.
  const round = useRef<{ index: number; others: DrawPrizeDto[] } | null>(null);
  // Нет неоткрытых козочек — поле под замочком, но только после конца раунда, чтобы не перекрыть
  // показ выигрыша последней козочки.
  const noGoats = !locked && (balance ?? 0) === 0 && !running;
  const fieldLocked = locked || noGoats;
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      for (const id of timeouts.current) clearTimeout(id);
    };
  }, []);
  const schedule = (fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timeouts.current.push(id);
    return id;
  };
  const setCell = (index: number, patch: Partial<Cell>) => setCells((cs) => cs.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const openCard = (index: number, point: { x: number; y: number }) => {
    // running выставляется до запроса: второй клик во время открытия не тратит ещё одну козочку.
    if (fieldLocked || running || cells[index].status !== 'closed') return;
    setRunning(true);
    // Ответ розыгрыша идёт до секунды — карточка покачивается, а надпись говорит, что подарок открывается.
    setRoundNote(t('gifts.roundNoteOpening'));
    setCell(index, { status: 'busy' });
    const startedAt = Date.now();
    draw.mutate(undefined, {
      onSuccess: (result) => {
        round.current = { index, others: result.cells };
        schedule(() => {
          setCell(index, { status: 'open', prize: result.prize });
          setRoundNote('');
          burst(point.x, point.y, 110, 10);
          schedule(() => setWinModal({ prize: result.prize, popupMessage: result.popupMessage }), 600);
        }, Math.max(0, OPEN_ANIMATION_MS - (Date.now() - startedAt)));
      },
      onError: (err) => {
        const code = drawErrorCode(err);
        setCell(index, { status: 'closed' });
        setRoundNote(code ? t(`gifts.drawError.${code}`) : t('gifts.drawError.network'));
        setRunning(false);
      },
    });
  };

  const closeWinModal = () => {
    setWinModal(null);
    schedule(() => revealAll(), 350);
  };

  const revealAll = () => {
    const current = round.current;
    if (!current) return;
    setRoundNote(t('gifts.roundNoteReveal'));
    setCell(current.index, { winner: true });
    let k = 0;
    for (let i = 0; i < CELL_COUNT; i++) {
      if (i === current.index || current.others.length === 0) continue;
      const prize = current.others[k++ % current.others.length];
      schedule(() => setCell(i, { status: 'open', prize, ghost: true }), i * 90);
    }

    schedule(() => {
      setRoundNote(t('gifts.roundNoteShuffle'));
      for (let i = 0; i < CELL_COUNT; i++) {
        schedule(() => setCell(i, { status: 'closed', prize: null, ghost: false, winner: false }), i * 70);
      }
      schedule(() => {
        setShuffle(true);
        schedule(() => {
          setShuffle(false);
          setRoundNote('');
          round.current = null;
          setRunning(false);
        }, 700);
      }, CELL_COUNT * 70 + 700);
    }, 2600);
  };

  const myGifts = awards.data?.items ?? [];
  const visibleGifts = showAllMyGifts ? myGifts : myGifts.slice(0, 3);

  return (
    <section className="sec s-cream round" id="gifts">
      <div className="wrap">
        <div className="ibanner rv">
          <div className="ibl">
            <span className="instant">{t('gifts.banner.badge')}</span>
            <h2>
              {t('gifts.banner.titleLine1')}
              <br />
              {t('gifts.banner.titleLine2')}
            </h2>
            <p>{t('gifts.banner.text')}</p>
          </div>
          <IScene />
        </div>
        <p className="keepline rv">{t('gifts.keepLine')}</p>
        <div className="herd rv">
          <span className="hl">{t('gifts.herdLabel')}</span>
          <span className="hcount">
            <i className="ico i-run" />
            {locked ? <LockedBadge /> : <b>{balance ?? 0}</b>}
          </span>
        </div>
        <div className={roundNote ? 'roundnote show' : 'roundnote'}>{roundNote}</div>
        <div className={['gifts', shuffle && 'shuffle', fieldLocked && 'locked'].filter(Boolean).join(' ')}>
          {cells.map((cell, i) => (
            <GiftCell key={i} index={i} cell={cell} onOpen={openCard} disabled={fieldLocked} ariaLabel={t('gifts.cardAria', { number: i + 1 })} />
          ))}
          {fieldLocked && (
            <div className="lockcover">
              <LockedBadge hint reason={locked ? undefined : 'noGoats'} />
            </div>
          )}
        </div>
        <div className="limit rv">{t('gifts.limitNote')}</div>
      </div>
      <div className="wrap">
        <div className="my rv" id="my">
          <div className="myhead">
            <div>
              <h3>
                {t('gifts.my.title')} {!locked && <span className="cnt">{myGifts.length}</span>}
              </h3>
              <p>{t('gifts.my.text')}</p>
            </div>
            {myGifts.length > 3 && (
              <button type="button" className={showAllMyGifts ? 'allbtn open' : 'allbtn'} onClick={() => setShowAllMyGifts((v) => !v)}>
                {showAllMyGifts ? t('gifts.my.collapseBtn') : t('gifts.my.allBtn')} <span className="ar">→</span>
              </button>
            )}
          </div>
          <div className="mygrid">
            {locked ? (
              <div className="lockrow">
                <LockedBadge hint />
              </div>
            ) : myGifts.length === 0 ? (
              <div className="empty">{t('gifts.my.empty')}</div>
            ) : (
              visibleGifts.map((g) => <MyGiftCard key={g.awardId} award={g} />)
            )}
          </div>
        </div>
      </div>

      {winModal && <WinModal prize={winModal.prize} popupMessage={winModal.popupMessage} onClose={closeWinModal} />}
    </section>
  );
}

function GiftCell({
  cell,
  onOpen,
  index,
  disabled,
  ariaLabel,
}: {
  cell: Cell;
  onOpen: (i: number, point: { x: number; y: number }) => void;
  index: number;
  disabled: boolean;
  ariaLabel: string;
}) {
  const classes = ['gift', 'rv'];
  if (cell.status === 'open') classes.push('open');
  if (cell.status === 'busy') classes.push('busy');
  if (cell.ghost) classes.push('ghost');
  if (cell.winner) classes.push('winner');

  return (
    <div className={classes.join(' ')}>
      <div className="in">
        <div className="f">
          <i className="ico i-bow" />
          <i className="ico i-goat gmini" />
        </div>
        <div className="b">{cell.prize && <GiftBack prize={cell.prize} />}</div>
      </div>
      <button type="button" aria-label={ariaLabel} disabled={disabled} onClick={(e) => onOpen(index, { x: e.clientX, y: e.clientY })} />
    </div>
  );
}

function GiftBack({ prize }: { prize: DrawPrizeDto }) {
  return (
    <>
      <PrizeTypeImage prize={prize} className="ptype" />
      <span className="pn">{prize.name}</span>
    </>
  );
}

/** Карточка «Мои подарки»: по нажатию раскрывает сообщение приза; без сообщения не раскрывается (add-prize-win-messages). */
function MyGiftCard({ award }: { award: LandingAward }) {
  const { t } = useTranslation('landing');
  const [open, setOpen] = useState(false);
  const { prize, message } = award;
  const toggle = message
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': open,
        'aria-label': t('gifts.my.messageAria', { name: prize.name }),
        onClick: () => setOpen((v) => !v),
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        },
      }
    : {};
  return (
    <article className={message ? 'gcard new has-msg' : 'gcard new'}>
      <div className="gtop" {...toggle}>
        <div className="gimg em">
          <PrizeTypeImage prize={prize} />
        </div>
        <div className="gtx">
          <h4>{prize.name}</h4>
          {prize.fullName !== prize.name && <div className="gs">{prize.fullName}</div>}
          <PrizeBadge prize={prize} />
        </div>
      </div>
      {message && open && <MessageText text={message} className="gbot gmsg" />}
    </article>
  );
}

function PrizeBadge({ prize }: { prize: DrawPrizeDto }) {
  const { t } = useTranslation('landing');
  switch (prize.category) {
    case 'PROMO':
      return (
        <span className="badge b-ok">
          <i>✓</i>
          {t('gifts.my.badgeReady')}
        </span>
      );
    case 'BONUS':
      return (
        <span className="badge b-wait">
          <i>•</i>
          {t('gifts.my.badgeWait')}
        </span>
      );
    case 'BUSINESS':
      return (
        <span className="badge b-wait">
          <i>•</i>
          {t('gifts.my.badgeContact')}
        </span>
      );
    default:
      return (
        <span className="badge b-wait">
          <i>•</i>
          {t('gifts.my.badgeSoon')}
        </span>
      );
  }
}

function IScene() {
  const reduced = prefersReducedMotion();
  return (
    <div className="iscene" aria-hidden="true">
      <i className="sglow" />
      <div className="sbox">
        <div className="slid">
          <i className="ico i-bow" />
        </div>
        <div className="sbody" />
      </div>
      <i className="ico i-run sgoat" />
      {!reduced && (
        <>
          <span className="tkt t1">🎧</span>
          <span className="tkt t2">−20%</span>
          <span className="tkt t3">💰</span>
          <span className="tkt t4">📱</span>
        </>
      )}
      <i className="spk s1" />
      <i className="spk s2" />
      <i className="spk s3" />
      <div className="sgoats">
        <i className="ico i-goat" />
        <i className="ico i-goat" />
        <i className="ico i-goat" />
      </div>
    </div>
  );
}
